/**
 * SelectViewPage
 *
 * Shown after login for users who have BOTH a citizen and a professional
 * affiliation. The user picks which view to enter; we persist that choice as
 * `accountType` in localStorage and hard-navigate to the matching dashboard.
 *
 * Single-view users never reach this page (LoginPage routes them straight to
 * their dashboard). If a single-view user lands here directly we redirect them
 * to the right place. If an unauthenticated visitor lands here we send them to
 * /login.
 *
 * The choice is per-session. The Pro layout's profile menu offers a direct
 * "Citizen View" link for dual-access accounts, which calls the same helpers
 * below rather than routing back through this page.
 */
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Briefcase, User } from 'lucide-react';
import { getAvailableViews } from '@shared/auth/session';
import { switchToCitizenView, switchToProfessionalView } from '../utils/switchView';

const SelectViewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '';

  let token: string | null = null;
  try { token = localStorage.getItem('token'); } catch { /* Safari private browsing */ }
  const availableViews = getAvailableViews();
  const hasCitizen = availableViews.includes('citizen');
  const hasProfessional = availableViews.includes('professional');

  // Defensive routing: only the dual-access case should render this page.
  useEffect(() => {
    if (!token) {
      const target = returnUrl
        ? `/login?returnUrl=${encodeURIComponent(returnUrl)}`
        : '/login';
      navigate(target, { replace: true });
      return;
    }
    if (hasCitizen && !hasProfessional) {
      pickCitizen();
      return;
    }
    if (hasProfessional && !hasCitizen) {
      pickProfessional();
      return;
    }
    if (!hasCitizen && !hasProfessional) {
      // No views recorded — force a fresh login so the server tells us again.
      navigate('/login', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared with the Pro profile menu's Citizen View link — see utils/switchView.
  const pickCitizen = () => switchToCitizenView(returnUrl);
  const pickProfessional = () => switchToProfessionalView(returnUrl);

  // While the effect is redirecting a single-view user, render nothing visible.
  if (!token || !(hasCitizen && hasProfessional)) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background-dark-primary px-4">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold font-gilroy text-primary-700 dark:text-primary-dark-300">
            Welcome back
          </h1>
          <p className="mt-2 text-text-secondary dark:text-text-dark-secondary">
            Your account has access to two views. Which would you like to use?
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={pickProfessional}
            className="card text-left hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary-700 dark:text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                Professional
              </h2>
            </div>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Legislation tracking, lobbying, calendar, reports, and admin tools for your organization.
            </p>
          </button>

          <button
            type="button"
            onClick={pickCitizen}
            className="card text-left hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <User className="w-5 h-5 text-primary-700 dark:text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                Citizen
              </h2>
            </div>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Track Colorado legislation, browse public reports, and follow what's moving this week.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectViewPage;
